import { useLocalStorage } from 'react-use';
import { createContext, type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { type QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@ev-tech/user-account-modules';
import { type SelfDescribingJson } from '@ev-tech/snowplow-library';
import { type Language, type MarketCountryCode, type SearchApi, type SearchListing } from '@ev-tech/search-modules-api';

import { type TrackingConfig } from './AppContextProvider';
import { getWatchlistElementId, trackWatchlistToggle } from '../utils/tracking';
import { type PendingUserAccountItem, PendingUserAccountItemType } from '../types/types';
import { useUserAccount } from '../hooks/useUserAccount';
import { getWatchlistQueryKey } from '../hooks/queries/queryKeys';
import { PENDING_USER_ACCOUNT_ITEM_KEY } from '../consts/consts';
import { Trigger } from '../components/UserAccountModal/UserAccountModal';

export const WatchlistAction = {
  ADD: 'add',
  REMOVE: 'remove',
} as const;

export type WatchlistAction = (typeof WatchlistAction)[keyof typeof WatchlistAction];

export const WatchlistSource = {
  DEFAULT: 'default',
  PREVIEW: 'preview',
  WATCHLIST: 'watchlist',
} as const;

export type WatchlistSource = (typeof WatchlistSource)[keyof typeof WatchlistSource];

export type WatchlistItem = { id: string };

export type WatchlistContextType = {
  watchlist: WatchlistItem[];
  getIsInWatchlist: (propertyId: string) => boolean;
  onWatchlistClick?: (property: SearchListing) => void;
  handleWatchlistClick: (property: SearchListing) => void;
};

export const WatchlistContext = createContext<WatchlistContextType>({
  watchlist: [],
  getIsInWatchlist: () => false,
  onWatchlistClick: () => {},
  handleWatchlistClick: () => {},
});

type WatchlistProviderProps = PropsWithChildren<{
  watchlist?: WatchlistItem[];
  onWatchlistClick?: (property: SearchListing) => void;
  queryClient: QueryClient;
  language: Language;
  countryCode: MarketCountryCode;
  api: SearchApi;
  tracking?: TrackingConfig;
  additionalTrackingContexts?: (SelfDescribingJson | ((property: SearchListing) => SelfDescribingJson))[];
  onCreate?: (status: 'success' | 'error', property: SearchListing, error?: Error) => void;
  onDelete?: (status: 'success' | 'error', property: SearchListing, error?: Error) => void;
}>;

export const WatchlistProvider = ({
  children,
  watchlist,
  onWatchlistClick,
  queryClient,
  language,
  countryCode,
  api,
  tracking,
  additionalTrackingContexts = [],
  onCreate,
  onDelete,
}: WatchlistProviderProps) => {
  const { openUserAccountModal } = useUserAccount();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const authenticatedApi = useMemo(() => api.withConfig({ getToken }), [api, getToken]);
  const [pendingItemToSave, setPendingItemToSave, removePendingItemToSave] = useLocalStorage<PendingUserAccountItem | null>(
    PENDING_USER_ACCOUNT_ITEM_KEY,
  );
  const { data: watchlistData } = useQuery({
    queryKey: getWatchlistQueryKey(),
    queryFn: async () => {
      const response = await authenticatedApi.getFavorites();
      if (response.status === 'success') {
        return response.data.listingIds?.map((id) => ({ id })) ?? [];
      }
      return [];
    },

    enabled: isLoaded && Boolean(isSignedIn),
    initialData: watchlist ?? queryClient.getQueryData<WatchlistItem[]>(getWatchlistQueryKey()),
  });
  const watchlistSet = useMemo(() => new Set(watchlistData?.map((item) => item.id) ?? []), [watchlistData]);

  const getIsInWatchlist = useCallback((propertyId: string) => watchlistSet.has(propertyId), [watchlistSet]);

  const { mutate: createFavorites } = useMutation({
    mutationFn: async ({ id }: SearchListing) => {
      return authenticatedApi.createFavorites({
        listingIds: [id],
        language,
      });
    },
    onMutate: async ({ id }: SearchListing) => {
      await queryClient.cancelQueries({ queryKey: getWatchlistQueryKey() });
      const previousWatchlist = queryClient.getQueryData<WatchlistItem[]>(getWatchlistQueryKey());
      queryClient.setQueryData<WatchlistItem[]>(getWatchlistQueryKey(), (old) => [...(old ?? []), { id }]);
      return { previousWatchlist };
    },
    onError: (error, property, context) => {
      if (context?.previousWatchlist) {
        queryClient.setQueryData(getWatchlistQueryKey(), context.previousWatchlist);
      }
      onCreate?.('error', property, error);
    },
    onSuccess: (data, property, context) => {
      if (data.status === 'success') {
        const listingIds = data.data.listingIds ?? [];
        queryClient.setQueryData(
          getWatchlistQueryKey(),
          listingIds.map((id) => ({ id })),
        );
        onCreate?.('success', property);
      } else {
        if (context.previousWatchlist) {
          queryClient.setQueryData(getWatchlistQueryKey(), context.previousWatchlist);
          onCreate?.('error', property, data.error);
        }
      }
    },
  });

  const { mutate: deleteFavorites } = useMutation({
    mutationFn: async ({ id }: SearchListing) => {
      return authenticatedApi.deleteFavoriteByListingId(id);
    },
    onMutate: async ({ id }: SearchListing) => {
      await queryClient.cancelQueries({ queryKey: getWatchlistQueryKey() });
      const previousWatchlist = queryClient.getQueryData<WatchlistItem[]>(getWatchlistQueryKey());
      queryClient.setQueryData<WatchlistItem[]>(getWatchlistQueryKey(), (old) => old?.filter((item) => item.id !== id) ?? []);
      return { previousWatchlist };
    },
    onError: (error, property, context) => {
      if (context?.previousWatchlist) {
        queryClient.setQueryData(getWatchlistQueryKey(), context.previousWatchlist);
      }
      onDelete?.('error', property, error);
    },
    onSuccess: (data, property, context) => {
      if (data.status === 'success') {
        queryClient.invalidateQueries({ queryKey: getWatchlistQueryKey() });
        onDelete?.('success', property);
      } else {
        if (context.previousWatchlist) {
          queryClient.setQueryData(getWatchlistQueryKey(), context.previousWatchlist);
        }
        onDelete?.('error', property, data.error);
      }
    },
  });

  const trackWatchlistEvent = useCallback(
    (property: SearchListing, action: WatchlistAction, source: WatchlistSource) => {
      trackWatchlistToggle({
        property,
        action,
        source,
        language,
        countryCode,
        isSignedIn: isSignedIn ?? false,
        tracking: tracking ?? {},
        additionalTrackingContexts,
      });
    },
    [language, countryCode, isSignedIn, tracking, additionalTrackingContexts],
  );

  const handleWatchlistClick = useCallback(
    (property: SearchListing) => {
      const isInWatchlist = getIsInWatchlist(property.id);

      if (isSignedIn === false) {
        const triggerId = getWatchlistElementId({ property, action: WatchlistAction.ADD, source: WatchlistSource.DEFAULT });

        setPendingItemToSave({ type: PendingUserAccountItemType.WATCHLIST, data: property });
        return openUserAccountModal({ signInTrigger: Trigger.Favorites, triggerId });
      } else {
        if (isInWatchlist) {
          deleteFavorites(property);
        } else {
          createFavorites(property);
        }
        trackWatchlistEvent(property, isInWatchlist ? WatchlistAction.REMOVE : WatchlistAction.ADD, WatchlistSource.DEFAULT);
      }
    },
    [isSignedIn, openUserAccountModal, getIsInWatchlist, createFavorites, deleteFavorites, trackWatchlistEvent, setPendingItemToSave],
  );

  useEffect(() => {
    if (isLoaded && isSignedIn === false) {
      queryClient.setQueryData(getWatchlistQueryKey(), []);
      return;
    }

    if (!pendingItemToSave) return;

    if (pendingItemToSave.type !== PendingUserAccountItemType.WATCHLIST || !pendingItemToSave.data) return;

    const property = pendingItemToSave.data;

    if (!getIsInWatchlist(property.id) && isSignedIn) {
      handleWatchlistClick(property);
      removePendingItemToSave();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, pendingItemToSave]);

  const value = useMemo(
    () => ({
      watchlist: watchlistData ?? [],
      getIsInWatchlist,
      onWatchlistClick,
      handleWatchlistClick,
    }),
    [watchlistData, getIsInWatchlist, onWatchlistClick, handleWatchlistClick],
  );
  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
};
